import { 
  OfflineCollectionSchema, 
  OfflineCollectionQueryParams, 
  OfflineCollectionDetailQueryParams,
  MatchTransactionRequest 
} from '../types/offline-collections';
import { getAuthToken } from '../utils/auth';

const API_BASE_URL = import.meta.env.VITE_APP_API_URL;

const OFFLINE_COLLECTIONS_ENDPOINT = `${API_BASE_URL}/admin/offline-collections`;

export const getOfflineCollections = async (params?: OfflineCollectionQueryParams): Promise<{
  collections: OfflineCollectionSchema[];
  next_cursor?: string;
}> => {
  const queryParams = new URLSearchParams();
  
  if (params?.amount) queryParams.append('amount', params.amount);
  if (params?.currency) queryParams.append('currency', params.currency);
  if (params?.status) queryParams.append('status', params.status);
  if (params?.from) queryParams.append('from', params.from);
  if (params?.to) queryParams.append('to', params.to);
  if (params?.next_cursor) queryParams.append('next_cursor', params.next_cursor);

  const url = `${OFFLINE_COLLECTIONS_ENDPOINT}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Authorization': `ClientBearer ${getAuthToken()}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch offline collections: ${response.statusText}`);
  }

  const data = await response.json();
  
  // Handle empty response or different response structure
  return {
    collections: data.collections || data.offline_collections || [],
    next_cursor: data.next_cursor
  };
};

export const getOfflineCollection = async (id: string, params?: OfflineCollectionDetailQueryParams): Promise<OfflineCollectionSchema> => {
  const queryParams = new URLSearchParams();
  
  if (params?.include_matching_txns) queryParams.append('include_matching_txns', params.include_matching_txns);

  const url = `${OFFLINE_COLLECTIONS_ENDPOINT}/${id}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Authorization': `ClientBearer ${getAuthToken()}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch offline collection: ${response.statusText}`);
  }

  return response.json();
};

export const matchTransaction = async (id: string, data: MatchTransactionRequest): Promise<OfflineCollectionSchema> => {
  // Note: The endpoint might be 'pffline-collections' based on the documentation typo
  // If this fails, we might need to use the corrected endpoint
  const response = await fetch(`${OFFLINE_COLLECTIONS_ENDPOINT}/${id}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `ClientBearer ${getAuthToken()}`,
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error(`Failed to match transaction: ${response.statusText}`);
  }

  return response.json();
};
